import { Injectable } from '@angular/core';
import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ExternalFormConfig, ExternalFormRegisterResult, TokenExpiredResult } from '../../_models/external-form.model';
import { EmpresaScmInfo, RegisterBusinessPartnerRequest } from '../../_views/create-supplier/create-supplier.model';

/**
 * Servicio para el registro externo de proveedores mediante token
 * Comunica con el BFF para operaciones de formulario externo v1
 */
@Injectable({
  providedIn: 'root',
})
export class ExternalFormService {
  private apiUrl = `${environment.urlApi}/external-form`;

  constructor(private http: HttpClient) {}

  /**
   * Obtiene la configuración del formulario externo para un token específico
   * @param tokenId Token de acceso temporal
   * @returns Observable con la configuración del formulario
   */
  getExternalFormConfig(tokenId: string): Observable<ExternalFormConfig | TokenExpiredResult> {
    return this.http.get<ExternalFormConfig>(`${this.apiUrl}/${tokenId}`, {
      observe: 'response',
    }).pipe(
      map((response) => {
        if (response.status === 200 && response.body) {
          return response.body;
        }
        return {
          expired: true,
          message: 'Token no válido',
        } as TokenExpiredResult;
      }),
      catchError((error) => {
        // Manejar específicamente el error 410 Gone
        if (error.status === HttpStatusCode.Gone) {
          return of({
            expired: true,
            message: 'El enlace ha expirado o ya fue utilizado',
          } as TokenExpiredResult);
        }
        // Manejar otros errores
        return throwError(() => error);
      })
    );
  }

  /**
   * Envía el formulario de registro externo de proveedor
   * @param tokenId Token de acceso temporal
   * @param data Datos del formulario de registro
   * @returns Observable con el resultado del registro
   */
  submitExternalForm(
    tokenId: string,
    data: RegisterBusinessPartnerRequest
  ): Observable<ExternalFormRegisterResult> {
    return this.http
      .post<ExternalFormRegisterResult>(`${this.apiUrl}/submit/${tokenId}`, data)
      .pipe(
        catchError((error) => {
          // Manejar errores específicos
          if (error.status === HttpStatusCode.Gone) {
            return of({
              success: false,
              message: 'El enlace ha expirado o ya fue utilizado',
            } as ExternalFormRegisterResult);
          }
          // Re-lanzar otros errores
          return throwError(() => error);
        })
      );
  }

  /**
   * Obtiene la información de empresa desde el WS get-info-empresa-scm
   * @param rut RUT de la empresa (formato: 12345678-9)
   * @param tokenId
   * @returns Observable con la información de la empresa
   */
  getInfoEmpresaScm(rut: string, tokenId: string): Observable<EmpresaScmInfo> {
    return this.http
      .get<EmpresaScmInfo>(`${this.apiUrl}/get-info-empresa-scm/${rut}/${tokenId}`)
      .pipe(
        map((data) => data),
        catchError((error) => {
          console.log('No se encontró información de empresa SCM para RUT:', rut);
          return throwError(() => new Error('NOT_FOUND'));
        })
      );
  }
}
